import { z } from 'zod';

let token = '';
export function setAuthToken(t: string): void { token = t; }
export function getAuthToken(): string { return token; }

export const MeSchema = z.object({
  playerId: z.string(),
  displayName: z.string(),
  friendCode: z.string(),
  currencies: z.object({ gold: z.number(), tickets: z.number() }),
  defenseTeam: z.unknown().nullable(),
  ladderPts: z.number(),
  tier: z.string(),
  tutorialCompletedAt: z.string().nullable().optional(),
  dailyPullsLeft: z.number().optional(),
  roster: z.array(z.unknown())
});
export type Me = z.infer<typeof MeSchema>;

export const PullSchema = z.object({
  memberId: z.string(),
  rarity: z.enum(['N', 'R', 'SR', 'SSR']),
  isDuplicate: z.boolean(),
  shardsAwarded: z.number()
});
export type PullResult = z.infer<typeof PullSchema>;

export const CampaignMatchSchema = z.object({
  matchId: z.string(),
  result: z.unknown(), // MatchResult shape from @stanwars/sim — kept loose at API boundary
  // Server returns null when the stage was already cleared at this star count or better
  // (no improvement → no reward issued). Treat null as empty rewards.
  rewards: z.object({
    gold: z.number(),
    tickets: z.number(),
    shards: z.array(z.object({ memberId: z.string(), count: z.number() }))
  }).nullable()
});

export const DailyMatchSchema = z.object({
  matchId: z.string(),
  result: z.unknown(),
  themeName: z.string(),
  attemptsLeft: z.number()
});
export type DailyMatchResult = z.infer<typeof DailyMatchSchema>;

export const LadderMatchSchema = z.discriminatedUnion('matched', [
  z.object({ matched: z.literal(false), reason: z.string() }),
  z.object({
    matched: z.literal(true),
    matchId: z.string(),
    result: z.unknown(),
    ladderDelta: z.number(),
    newPts: z.number(),
    newTier: z.string(),
    promoted: z.boolean()
  })
]);
export type LadderMatchResult = z.infer<typeof LadderMatchSchema>;

export const LadderTopSchema = z.object({
  entries: z.array(z.object({
    rank: z.number(),
    playerId: z.string(),
    displayName: z.string(),
    tier: z.string(),
    pts: z.number()
  }))
});
export type LadderTop = z.infer<typeof LadderTopSchema>;

export const ReplaySchema = z.object({
  id: z.string(),
  kind: z.string(),
  lineupA: z.unknown(),
  lineupB: z.unknown(),
  result: z.unknown(),
  createdAt: z.string(),
  simVersion: z.string()
});
export type Replay = z.infer<typeof ReplaySchema>;

export const FriendAddSchema = z.object({
  added: z.literal(true),
  friend: z.object({ displayName: z.string(), tier: z.string() })
});
export type FriendAdd = z.infer<typeof FriendAddSchema>;

export const FriendMatchSchema = z.object({
  matchId: z.string(),
  result: z.unknown()
});
export type FriendMatchResult = z.infer<typeof FriendMatchSchema>;

const TutorialCompleteSchema = z.object({
  ok: z.literal(true),
  tutorialCompletedAt: z.string()
});

const PushSubscribeSchema = z.object({
  ok: z.literal(true),
  id: z.number().optional(),
  detail: z.string().optional()
});

const API_BASE = '/stanwars/api';

async function request<T>(
  path: string,
  schema: z.ZodSchema<T>,
  init?: RequestInit
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((init?.headers as Record<string, string>) ?? {})
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return schema.parse(await res.json());
}

export const api = {
  me: {
    get: () => request('/me', MeSchema),
    updateLoadout: (memberId: string, loadout: { passiveId: string; activeIds: [string, string]; ultimateId: string }) =>
      request(`/me/members/${encodeURIComponent(memberId)}/loadout`, z.object({ ok: z.literal(true) }), {
        method: 'PATCH', body: JSON.stringify(loadout)
      }),
    completeTutorial: () =>
      request('/me/tutorial-complete', TutorialCompleteSchema, { method: 'PATCH' }),
    subscribePush: (subscription: PushSubscriptionJSON) =>
      request('/me/push-subscriptions', PushSubscribeSchema, {
        method: 'POST',
        body: JSON.stringify({ subscription })
      })
  },
  pull: {
    post: (body: { requestId: string; useFree: boolean }) =>
      request('/pull', PullSchema, { method: 'POST', body: JSON.stringify(body) })
  },
  match: {
    campaign: (body: { stageId: string; lineup: { memberIds: string[]; requestId: string } }) =>
      request('/match/campaign', CampaignMatchSchema, { method: 'POST', body: JSON.stringify(body) }),
    daily: (body: { dateKey: string; lineup: { memberIds: string[]; requestId: string } }) =>
      request('/match/daily', DailyMatchSchema, { method: 'POST', body: JSON.stringify(body) }),
    ladder: (body: { lineup: { memberIds: string[]; requestId: string } }) =>
      request('/match/ladder', LadderMatchSchema, { method: 'POST', body: JSON.stringify(body) }),
    replay: (id: string) => request(`/match/replay/${encodeURIComponent(id)}`, ReplaySchema)
  },
  ladder: {
    top: () => request('/ladder/top', LadderTopSchema)
  },
  friend: {
    add: (body: { code: string }) =>
      request('/friend/add', FriendAddSchema, { method: 'POST', body: JSON.stringify(body) }),
    match: (code: string, body: { lineup: { memberIds: string[]; requestId: string } }) =>
      request(`/friend/match/${encodeURIComponent(code)}`, FriendMatchSchema, {
        method: 'POST', body: JSON.stringify(body)
      })
  }
};
